// Label helpers for the Grants page: turning raw object ids and subject
// strings into human-readable names. The company segment of an object id is
// opaque (a GUID in the live stores, a slug in fixtures), so company display
// names come from the catalog and fall back to the raw segment.
import type { AccessPrincipal, AccessStoreRole } from "~/api/grants";
import type { CatalogCompany } from "~/api/types";

export type GrantObjectType = "organization" | "company" | "platform";

export const OBJECT_TYPE_LABEL: Record<GrantObjectType, string> = {
  organization: "Organization",
  company: "Company",
  platform: "Platform",
};

export function objectTypeOf(objectId: string): GrantObjectType | null {
  const type = objectId.slice(0, objectId.indexOf(":"));
  if (type === "organization" || type === "company" || type === "platform") {
    return type;
  }
  return null;
}

// "company:guid" → ["guid"]; "platform:guid/apm" → ["guid", "apm"]
function idSegments(objectId: string): string[] {
  return objectId.slice(objectId.indexOf(":") + 1).split("/");
}

function companyDisplayName(
  segment: string,
  companies: CatalogCompany[] | undefined,
): string {
  const hit = companies?.find((c) => c.id === segment);
  return hit?.displayName ?? segment;
}

export function objectLabel(
  objectId: string,
  companies?: CatalogCompany[],
): string {
  const segments = idSegments(objectId);
  switch (objectTypeOf(objectId)) {
    case "company":
      return companyDisplayName(segments[0], companies);
    case "platform":
      return `${companyDisplayName(segments[0], companies)} / ${segments.slice(1).join("/")}`;
    case "organization":
      return segments[0];
    default:
      return objectId;
  }
}

// The bare id of a subject string: strips the type prefix and, for groups,
// the `#member` userset suffix.
export function subjectId(subject: string): string {
  return subject.slice(subject.indexOf(":") + 1).split("#")[0];
}

export function subjectLabel(
  subject: string,
  info?: AccessPrincipal["info"],
): string {
  return info?.displayName ?? subjectId(subject);
}

export const ROLE_LABEL: Record<string, string> = {
  admin: "Admin",
  writer: "Writer",
  drafter: "Drafter",
  reader: "Reader",
};

// One row per subject in the access table: the strongest relation the subject
// holds, direct grants first. Indirect entries at or below a subject's own
// direct role carry no new information (the ladder implies downwards), so only
// a strictly stronger inherited relation is kept — same reasoning as
// ManagementAccessSection's direct/indirect split.
export interface AccessRow {
  subject: string;
  subjectType: AccessPrincipal["subjectType"];
  info: AccessPrincipal["info"];
  relation: AccessStoreRole;
  access: "direct" | "indirect";
  via: string | null;
}

const ROLE_RANK: Record<AccessStoreRole, number> = {
  admin: 3,
  writer: 2,
  drafter: 1,
  reader: 0,
};

export function flattenAccessRows(
  response: { [K in AccessStoreRole]?: AccessPrincipal[] } | undefined,
): { directRows: AccessRow[]; indirectRows: AccessRow[] } {
  // Strongest relation per subject, computed independently per access kind:
  // a subject can hold a direct reader while inheriting writer, and both are
  // true at once.
  const strongestByKind = (kind: "direct" | "indirect"): AccessRow[] => {
    const strongest = new Map<string, AccessRow>();
    for (const relation of ["admin", "writer", "drafter", "reader"] as const) {
      for (const principal of response?.[relation] ?? []) {
        if ((principal.access === "direct") !== (kind === "direct")) continue;
        if (!strongest.has(principal.subject)) {
          strongest.set(principal.subject, { ...principal, relation });
        }
      }
    }
    return [...strongest.values()];
  };

  const directRows = strongestByKind("direct");
  const directRankBySubject = new Map<string, number>(
    directRows.map((row) => [row.subject, ROLE_RANK[row.relation]]),
  );
  // An indirect entry at or below the subject's own direct role carries no
  // new information (the ladder implies downwards); only a strictly stronger
  // inherited relation is worth a row.
  const indirectRows = strongestByKind("indirect").filter((row) => {
    const directRank = directRankBySubject.get(row.subject);
    return directRank === undefined || ROLE_RANK[row.relation] > directRank;
  });

  directRows.sort((a, b) =>
    ROLE_RANK[a.relation] !== ROLE_RANK[b.relation]
      ? ROLE_RANK[b.relation] - ROLE_RANK[a.relation]
      : subjectLabel(a.subject, a.info).localeCompare(
          subjectLabel(b.subject, b.info),
        ),
  );
  indirectRows.sort((a, b) =>
    subjectLabel(a.subject, a.info).localeCompare(
      subjectLabel(b.subject, b.info),
    ),
  );

  return { directRows, indirectRows };
}
